import type { OutletResource } from '@repo/api-client';

/**
 * What an outlet's kind and stock position are called on screen.
 *
 * The wire values are `GATE`, `RETAIL` and `ACTIVITY`, and the API is the only
 * thing that should ever see them. Nothing here restates the union — it is read
 * off `OutletResource`, so a fourth kind added to the document fails to compile
 * in this file rather than rendering as `SOMETHING_NEW` on a manager's screen.
 */
export type OutletKind = OutletResource['kind'];

/**
 * The kinds, in the order they are offered when creating one.
 *
 * Gate first because it is the outlet a park cannot open without, and activity
 * centre last because it is the one that behaves differently.
 */
export const OUTLET_KINDS: readonly OutletKind[] = ['GATE', 'RETAIL', 'ACTIVITY'];

/**
 * `Record` rather than a function with a default, so the exhaustiveness is the
 * type system's problem and not a runtime fallback nobody ever sees.
 */
const KIND_LABELS: Record<OutletKind, string> = {
  GATE: 'Gate',
  RETAIL: 'Shop',
  ACTIVITY: 'Activity centre',
};

export const labelForKind = (kind: OutletKind): string => KIND_LABELS[kind];

/**
 * The same kinds as a value-to-label map, for `<Select items={…}>`.
 *
 * Base UI resolves what the trigger shows from this prop and not from the
 * rendered `<SelectItem>`s, so a select given options but no `items` shows the
 * raw value — `RETAIL` rather than "Shop". Derived from `OUTLET_KINDS` so the
 * two cannot drift.
 */
export const OUTLET_KIND_ITEMS: ReadonlyArray<{ label: string; value: OutletKind }> =
  OUTLET_KINDS.map((kind) => ({ label: labelForKind(kind), value: kind }));

/** What each kind is for, shown under its option when creating an outlet. */
const KIND_HINTS: Record<OutletKind, string> = {
  GATE: 'Sells entry tickets and admits visitors.',
  RETAIL: 'Sells goods from stock — the doll shop, the plant counter.',
  ACTIVITY: 'Sells rides and activities. Takes money, holds no stock.',
};

export const hintForKind = (kind: OutletKind): string => KIND_HINTS[kind];

/**
 * How an outlet's stock position reads in the list.
 *
 * The two answers are deliberately parallel — a noun phrase each, the same
 * length, the same weight, neither one a negation of the other. The four
 * activity centres are full outlets that take real money and reconcile at day
 * close; they simply sell nothing off a shelf. Rendering that as an empty cell,
 * a crossed-out tick, or "No" against the shops' "Yes" would say they are the
 * lesser row in the table, which is exactly what this ticket asks it not to say.
 */
export const labelForStock = (carriesStock: boolean): string =>
  carriesStock ? 'Holds stock' : 'Sells no goods';

/**
 * Whether a kind ordinarily holds stock — the default offered when creating one,
 * and nothing stronger.
 *
 * `carriesStock` is a field of its own on both the create and update DTOs, and
 * the API says why in as many words: it is separate from kind because a future
 * outlet may not follow its kind. So this pre-fills the switch and the person
 * creating the outlet can disagree with it.
 */
export const ordinarilyCarriesStock = (kind: OutletKind): boolean => kind !== 'ACTIVITY';

/**
 * Whether an outlet has been retired.
 *
 * The nullable-timestamp annotation gap this was written around (`#209`) is
 * **closed** as of the 10 Aug 2026 regeneration: the field is a real
 * `string | null` and a screen may now say when. Nothing needs unwinding — the
 * predicate is still the right shape for the question it answers — but a screen
 * that wants the moment no longer has to pretend it cannot have it.
 */
export const isRetired = (outlet: OutletResource): boolean =>
  outlet.retiredAt !== null && outlet.retiredAt !== undefined;
